import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { getAttachment } from "slices/attachment";
import runtime from "config/runtime";

const NiceImage = ({ children, maxWidth = 200, maxHeight = 200 }) => {
  const dispatch = useDispatch();
  const attachmentId = children;
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      // Check if we have a valid attachment ID
      if (
        !attachmentId ||
        attachmentId === "Not Set" ||
        attachmentId === "undefined" ||
        attachmentId === "null" ||
        typeof attachmentId !== "string" ||
        attachmentId.trim() === ""
      ) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await dispatch(getAttachment({ id: attachmentId })).unwrap();
        const attachment = result.attachment || result;

        if (attachment && attachment.fileUrl) {
          // Construct the full URL from the fileUrl path
          // REACT_APP_API_URL is like "/api/v1" or "http://localhost:8080/api/v1"
          // We need to remove both "/api/v1" to get the base, since fileUrl is at root level
          const apiUrl = runtime.apiUrl;
          const baseURL = apiUrl.replace('/api/v1', '');
          const fullUrl = baseURL + attachment.fileUrl;

          setImageData({
            url: fullUrl,
            name: attachment.name,
            mimeType: attachment.mimeType,
          });
        } else {
          setError("Image URL not found");
        }
      } catch (err) {
        console.error("Error loading image:", err);
        setError("Failed to load image");
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [attachmentId, dispatch]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        {error}
      </Typography>
    );
  }

  if (!imageData) {
    return (
      <Typography variant="caption" color="text.secondary">
        No image
      </Typography>
    );
  }

  return (
    <Box
      component="img"
      src={imageData.url}
      alt={imageData.name || "Image"}
      onError={() => {
        setError("Failed to load image from server");
      }}
      sx={{
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        width: "auto",
        height: "auto",
        objectFit: "contain",
        borderRadius: 1,
      }}
    />
  );
};

export default NiceImage;
